// libraries
import React, { useState, useEffect } from 'react'

import dayjs from 'dayjs'
import 'dayjs/locale/nb'

// components
import Section from '/react4xp/components/Section'
import OverviewTopPart from '/react4xp/components/OverviewTopPart'
import Button from '/react4xp/components/Button'
import ArticleCard from '/react4xp/components/ArticleCard'

// svg
import { UxArrowDown } from '/react4xp/components/Svg/Ux/Arrow'

// prop types
export interface ArticlesOverviewWrapperProps {
	title?: string
    tags?: {
        id?: string
        name?: string
        title?: string
    }[]
    endpoint?: string
    startValues?: {
        page?: number
        pageSize?: string
        search?: string
        tags?: string[]
    }
    strings?: {
        search?: string
        searchButton?: string
        noResultsFound?: string
        loadMore?: string
    }
}

export default function ArticlesOverviewWrapper({
	title,
	tags,
	endpoint,
	startValues,
	strings
}: ArticlesOverviewWrapperProps) {

	const [articles, setArticles] = useState<any[]>([])
    const [page, setPage] = useState(startValues?.page || 1)
    const [inputValue, setInputValue] = useState(startValues?.search || '')
	const [searchTerm, setSearchTerm] = useState(startValues?.search || '')
    const [selectedTags, setSelectedTags] = useState<any[]>(startValues?.tags && tags.filter((tag)=> startValues.tags.includes(tag.title)) || [])
    const [hasNextPage, setHasNextPage] = useState(false)
	const [isLoading, setIsLoading] = useState(false)
	const [isLoadingMore, setIsLoadingMore] = useState(false)

    useEffect(() => {
        handleFetchArticles()
	}, [searchTerm, selectedTags, page])

    const handleFetchArticles = async ()=> {
        
        if(!endpoint) return

		const isLoadMoreRequest = isLoadingMore

		if (isLoadMoreRequest) {
			setIsLoadingMore(true)
		} else {
			setIsLoading(true)
		}
        
        const origin = window.location.origin
        const url = new URL(`${origin}${endpoint}`)
        const queryParams = new URLSearchParams()

        if (page) queryParams.append('page', String(page))
        if (startValues?.pageSize) queryParams.append('pageSize', startValues?.pageSize)
        if (searchTerm) queryParams.append('search', searchTerm)
        if (selectedTags.length) {
            selectedTags.forEach(tag => {
                queryParams.append('tags', tag.title)
            })
        }
    
        url.search = queryParams.toString()

		if (!isLoadMoreRequest) {
			updateCurrentUrl()
		}
        
        try {
            const response = await fetch(url)
            if (!response.ok) {
                throw new Error(`Network response was not ok: ${response.statusText}`)
            }
            const data = await response.json()
            
			setArticles((prevArticles) => {
				if (isLoadMoreRequest) {
					return [...prevArticles, ...data.hits]
				}

				return data.hits
			})

            setHasNextPage(data.hasNextPage)

        } catch (error) {
            console.error('There was a problem with the fetch operation:', error)
        }

		setIsLoading(false)
		setIsLoadingMore(false)
    }

    const handleSelectTagFilter = (tag) => {
        setPage(1)
        setSelectedTags(prev => {
            const tagExists = prev.some(item => item.id === tag.id)
            if (tagExists) {
                return prev.filter(item => item.id !== tag.id)
            } else {
                return [...prev, { id: tag.id, title: tag.title }]
            }
        })
    }

	const handleSearchTerm = () => {
        setPage(1)
        setSearchTerm(inputValue)

        setTimeout(() => {
			const searchResultsElement = document.getElementById('search-results')
			if (searchResultsElement) {
				const headerOffset = 0
				const elementPosition = searchResultsElement.getBoundingClientRect().top + window.scrollY
				const offsetPosition = elementPosition - headerOffset
	
				window.scrollTo({
					top: offsetPosition,
					behavior: 'smooth'
				})
			}
		}, 100)
	}
    
	const handleLoadMoreArticles = () => {
        if(hasNextPage && !isLoadingMore){
			setIsLoadingMore(true)
            setPage((prevPage) => prevPage + 1)
        }
    }

    const updateCurrentUrl = () => {
        const origin = window.location.origin
        const updatedUrl = new URL(`${origin}${window.location.pathname}`)
        const queryParams = new URLSearchParams()

        if (searchTerm) queryParams.append('search', searchTerm)
        if (selectedTags.length) {
            selectedTags.forEach(tag => {
                queryParams.append('tags', tag.title)
            })
        }

        updatedUrl.search = queryParams.toString()
        window.history.pushState({}, '', updatedUrl)
    
    }

	return (
		<main
			id='main'
			className='page-overview'
		>

			<OverviewTopPart
				title={title}
				filters={tags}
				selectedFilters={selectedTags}
				onFilterClick={handleSelectTagFilter}
				search={{
					label: strings.search,
					searchButtonText: strings.searchButton,
					value: inputValue,
					isLoading,
					disabled: isLoading || isLoadingMore,
					onSearch: handleSearchTerm,
					onChange: (e) => {
						setInputValue(e.target.value)
					}
				}}
			/>

			<Section marginTop='none'>
				<div className='container'>

					<div id='search-results' className='overview-grid'>

						{articles.length > 0 ? (
							<div className='overview-grid__wrapper'>
								{articles.map((article, i) => (
									<ArticleCard
										key={i}
										href={article.url?.href}
                                        external={false}
                                        src={article.imageSkyr?.link} // 550px x 450px
                                        srcMedium={article.imageSkyrMedium?.link} // 450px x 350px
                                        alt={article.imageSkyr?.alt}
                                        width={400}
                                        sizes='( max-width: 992px ) 100vw, 400px'
                                        date={dayjs(article.date).locale('nb').format('D. MMMM YYYY')}
                                        title={article.title}
                                        desc={article.intro}
									/>
								))}
							</div>
						) : (
							<div className='overview-grid__no-results'>
								<h2 className='h1-hero-small'>
									{strings.noResultsFound}
								</h2>
							</div>
						)}

						{hasNextPage && (
							<div className='overview-grid__load-more'>
								<Button
									isLoading={isLoadingMore}
									disabled={isLoadingMore}
									onClick={handleLoadMoreArticles}
								>
									{strings.loadMore} <UxArrowDown />
								</Button>
							</div>
						)}

					</div>

				</div>
			</Section>
			
		</main>
	)
}
